import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, Typography } from '@mui/material';
import {
  HoverPopover,
  InfoCard,
  MarkedDateCalendar,
  NavListCard,
} from '@core/components';
import { toProperCase } from '@core/utils/string';
import { type Order, getAllOrders } from '../../api/order';
import { getFullName } from '../utils/customer';
import { OPEN_ORDER_STATUSES, isDueDateApproaching } from '../utils/order';

type DueDayPopoverItem = {
  orders: Order[];
  day: Date;
};

const DueDateCalendar: React.FC = () => {
  const navigate = useNavigate();

  const { data: ordersByDay = new Map<string, Order[]>() } = useQuery({
    queryKey: ['orders', 'active-by-due-date'],
    queryFn: getAllOrders,
    select: (orders): Map<string, Order[]> => {
      const byDay = new Map<string, Order[]>();
      for (const order of orders) {
        if (!order.dueDate || !OPEN_ORDER_STATUSES.includes(order.status)) {
          continue;
        }
        const key = order.dueDate.slice(0, 10);
        const bucket = byDay.get(key);
        if (bucket) {
          bucket.push(order);
        } else {
          byDay.set(key, [order]);
        }
      }
      return byDay;
    },
  });

  const markedDays = [...ordersByDay.entries()].map(([key, dayOrders]) => ({
    key,
    color: dayOrders.some(
      (order) =>
        order.dueDate != null &&
        isDueDateApproaching(order.dueDate, order.status)
    )
      ? 'error'
      : 'warning',
  }));

  return (
    <InfoCard title="Due Dates" icon={<CalendarTodayIcon />}>
      <HoverPopover<DueDayPopoverItem>
        trigger={({ openAt, scheduleClose }) => (
          <MarkedDateCalendar
            markedDays={markedDays}
            onMarkedDayHover={(anchorEl, day) => {
              const orders = ordersByDay.get(format(day, 'yyyy-MM-dd')) ?? [];
              if (orders.length === 0) {
                return;
              }
              openAt(anchorEl, { orders, day });
            }}
            onMarkedDayLeave={scheduleClose}
          />
        )}
      >
        {(item) => (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              px={0.5}
              pb={0.75}
            >
              Due {format(item.day, 'MMM d, yyyy')}
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.75}>
              {item.orders.map((order) => (
                <NavListCard
                  key={order.id}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {getFullName(order.customer)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {toProperCase(order.status)}
                  </Typography>
                </NavListCard>
              ))}
            </Box>
          </>
        )}
      </HoverPopover>
    </InfoCard>
  );
};

export default DueDateCalendar;
